import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { FaMapMarkerAlt, FaPhone, FaUser, FaCheck, FaMoneyBillWave, FaTruck, FaPlay, FaClock } from "react-icons/fa";
import { APP_PATH } from "../../../api/backendPath";
import logo from "../../../assets/images/logo.png";
import axios from "axios";
import { toast } from '../../../components/toast';
import { formatCustom as tzCustom, formatDateNumeric as tzDateNumeric } from "../../../utils/datetime";
import { formatPercent, formatWholeAmount } from "../../../utils/amountFormat";
import PublicPatientName from "../../../components/booking-patient-cells/PublicPatientName";

const PublicPhysioAppointmentDetail = () => {
  const { id: orderId } = useParams();

  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Workflow state
  const [isDispatched, setIsDispatched] = useState(false);
  const [isProcessStarted, setIsProcessStarted] = useState(false);
  const [isPaymentCollected, setIsPaymentCollected] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);

  // Estimated arrival time
  const [estimatedArrivalMinutes, setEstimatedArrivalMinutes] = useState(30);

  // Fetch order details
  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}${APP_PATH.physioPublicOrder}/${orderId}`
      );
      if (response?.data?.status && response.data.data) {
        const data = response.data.data;
        setOrderDetails(data);
        const status = data.status;
        const paymentStatus = data.paymentStatus || data.payment_status;
        setIsDispatched(['THERAPIST_DISPATCHED', 'IN_PROGRESS', 'COMPLETED'].includes(status));
        setIsProcessStarted(['IN_PROGRESS', 'COMPLETED'].includes(status));
        setIsPaymentCollected(paymentStatus === 'PAID' || paymentStatus === 'COMPLETED');
        setIsCompleted(status === 'COMPLETED');
      } else {
        setError("Order not found");
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load order details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (orderId) fetchOrderDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const handleWorkflowAction = async (action) => {
    try {
      const requestBody = { action };
      if (action === 'dispatch') {
        requestBody.estimatedArrivalMinutes = estimatedArrivalMinutes;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}${APP_PATH.physioPublicOrder}/${orderId}/workflow`,
        requestBody
      );
      if (response?.data?.status) {
        if (action === 'dispatch') {
          toast.success(`Therapist dispatched! Patient notified with estimated arrival: ${estimatedArrivalMinutes} minutes.`);
        } else if (action === 'start_process') {
          toast.success("Process started successfully");
        } else if (action === 'collect_payment') {
          toast.success("Payment collected & order completed");
        } else if (action === 'complete') {
          toast.success("Order completed successfully");
        }
        fetchOrderDetails();
      } else {
        toast.error(response?.data?.message || "Failed to update");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update");
    }
  };

  const generateNavigationLink = (address) => {
    if (!address) return "#";

    // Prefer precise coordinates when available, fall back to the text address.
    const hasCoords =
      address.latitude != null && address.latitude !== '' &&
      address.longitude != null && address.longitude !== '' &&
      !isNaN(Number(address.latitude)) && !isNaN(Number(address.longitude));
    if (hasCoords) {
      return `https://www.google.com/maps/dir/?api=1&destination=${address.latitude},${address.longitude}&travelmode=driving&dir_action=navigate`;
    }

    const parts = [
      address.houseFlatFloorNo || address.house_flat_floor_no,
      address.street,
      address.areaDistrict || address.area_district,
      address.city,
      address.governorate,
      address.country
    ].filter(Boolean);

    if (parts.length > 0) {
      return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(parts.join(", "))}&travelmode=driving&dir_action=navigate`;
    }
    return "#";
  };

  const formatPatientName = (patient) => {
    if (!patient) return "Patient";
    const parts = [patient.full_name, patient.surname].filter(Boolean);
    return parts.length > 0 ? parts.join(" ") : "Patient";
  };

  const formatTime = (time) => {
    if (!time) return 'TBD';
    const parts = time.split(':');
    if (parts.length >= 2) {
      const hour = parseInt(parts[0]);
      const minute = parts[1];
      const period = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      return `${displayHour}:${minute} ${period}`;
    }
    return time;
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', fontFamily: 'Inter, sans-serif' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: '40px', height: '40px', border: '4px solid #e5e7eb', borderTop: '4px solid #099494', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
          <p style={{ color: '#6b7280' }}>Loading appointment details...</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', fontFamily: 'Inter, sans-serif' }}>
        <p style={{ color: '#EF4444', fontSize: '16px' }}>{error}</p>
      </div>
    );
  }

  const isCOD = orderDetails?.paymentMethod?.toUpperCase() === 'COD';
  const isHomeVisit = orderDetails?.preferredLocation === 'HOME_VISIT';
  const transportationFee = isHomeVisit ? Number(orderDetails?.transportationFee || orderDetails?.transportation_fee || 0) : 0;
  const adminFeePercentagePublic = Number(orderDetails?.adminFeePercentage || orderDetails?.admin_fee_percentage || 0);
  const adminFeeAmountPublic = Number(orderDetails?.adminFeeAmount || orderDetails?.admin_fee_amount || 0);
  // Filter active services (exclude completed sessions for revisit orders)
  const allServices = orderDetails?.services || [];
  const currentSession = orderDetails?.currentSessionNumber || orderDetails?.current_session_number || 1;
  const isRevisitOrder = currentSession > 1;
  const activeServices = isRevisitOrder
    ? allServices.filter(s => (s.sessions_completed || 0) < (s.number_of_sessions || 1))
    : allServices;
  // If all sessions are complete, show all services with full totals
  const allSessionsComplete = isRevisitOrder && activeServices.length === 0;
  const displayServices = allSessionsComplete ? allServices : activeServices;
  // Calculate total: for revisit orders compute from active/display services, otherwise use backend total
  const backendTotal = Number(orderDetails?.totalAmount || orderDetails?.total_amount || 0);
  const servicesSubtotal = displayServices.reduce((sum, s) => sum + Number(s.price || 0), 0);
  const computedAdminFee = adminFeePercentagePublic > 0 ? ((servicesSubtotal + transportationFee) * adminFeePercentagePublic / 100) : adminFeeAmountPublic;
  const computedTotal = servicesSubtotal + transportationFee + computedAdminFee;
  const grossTotal = isRevisitOrder ? computedTotal : (backendTotal > 0 ? backendTotal : computedTotal);
  // Subtract any loyalty-point discount the patient redeemed so the cash amount to
  // collect (and the Total shown) is the net the patient actually owes — matching
  // the invoice. Loyalty applies regardless of payment method, including COD.
  const loyaltyDiscount = Number(orderDetails?.loyaltyDiscount || orderDetails?.loyalty_discount || 0);
  const totalAmount = Math.max(0, grossTotal - loyaltyDiscount);

  // Reusable card style
  const cardStyle = {
    background: '#fff',
    borderRadius: '12px',
    padding: '20px',
    border: '1px solid #e5e7eb',
    height: '100%',
    boxSizing: 'border-box'
  };

  const headingStyle = {
    fontSize: '14px',
    fontWeight: 600,
    color: '#099494',
    marginBottom: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    paddingBottom: '10px',
    borderBottom: '1px solid #f3f4f6'
  };

  return (
    <div style={{ fontFamily: 'Inter, sans-serif', background: '#f9fafb', minHeight: '100vh' }}>
      {/* Header with Logo */}
      <div style={{
        background: '#fff',
        borderBottom: '1px solid #e5e7eb',
        padding: '14px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
      }}>
        <img src={logo} alt="Tiryaq" style={{ height: '40px' }} />
      </div>

      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '24px' }}>
        {/* Title */}
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <h1 style={{ fontSize: '24px', fontWeight: 700, color: '#1f2937', margin: 0 }}>
            Appointment Details
          </h1>
          <p style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>
            Booking: {orderDetails?.bookingNumber || `PT-${orderId}`}
          </p>
          <span style={{
            display: 'inline-block', marginTop: '10px', padding: '5px 14px', borderRadius: '20px',
            fontSize: '12px', fontWeight: 600,
            background: isCompleted ? '#d1fae5' : isDispatched ? '#dbeafe' : '#fef3c7',
            color: isCompleted ? '#065f46' : isDispatched ? '#1e40af' : '#92400e',
          }}>
            {orderDetails?.status?.split('_').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ') || 'Pending'}
          </span>
        </div>

        {/* Row 1: 3 columns - Patient | Therapist | Payment */}
        <div data-public-grid-row1 style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '16px',
          marginBottom: '16px'
        }}>
          {/* Patient Info */}
          <div style={cardStyle}>
            <h3 style={headingStyle}>
              <FaUser /> Patient Information
            </h3>
            {/* Child booking: child + "Child" pill, then the account holder as the
                Guardian. Self booking: the same single name line as before. */}
            <PublicPatientName
              booking={orderDetails}
              guardianName={formatPatientName(orderDetails?.patient)}
              nameStyle={{ fontSize: '16px', fontWeight: 600, color: '#1f2937', margin: '0 0 6px' }}
            />
            {orderDetails?.patient?.mobile_number && (
              <a href={`tel:${orderDetails.patient.mobile_number}`} style={{ fontSize: '14px', color: '#099494', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <FaPhone size={12} /> {orderDetails.patient.mobile_number}
              </a>
            )}
          </div>

          {/* Therapist Info */}
          <div style={cardStyle}>
            <h3 style={headingStyle}>
              <FaUser /> Therapist Assigned
            </h3>
            {orderDetails?.therapist ? (
              <>
                <p style={{ fontSize: '16px', fontWeight: 600, color: '#1f2937', margin: '0 0 6px' }}>
                  {orderDetails.therapist.name || 'N/A'}
                </p>
                {orderDetails.therapist.mobile_number && (
                  <a href={`tel:${orderDetails.therapist.mobile_number}`} style={{ fontSize: '14px', color: '#099494', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <FaPhone size={12} /> {orderDetails.therapist.mobile_number}
                  </a>
                )}
                {orderDetails.therapist.gender && (
                  <p style={{ fontSize: '13px', color: '#6b7280', margin: '6px 0 0' }}>
                    Gender: {orderDetails.therapist.gender.charAt(0).toUpperCase() + orderDetails.therapist.gender.slice(1)}
                  </p>
                )}
              </>
            ) : (
              <p style={{ fontSize: '14px', color: '#9ca3af', margin: 0 }}>Not assigned yet</p>
            )}
          </div>

          {/* Payment Info */}
          <div style={cardStyle}>
            <h3 style={headingStyle}>
              <FaMoneyBillWave /> Payment
            </h3>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <span style={{ fontSize: '14px', color: '#374151' }}>Method</span>
              <span style={{
                fontSize: '12px', fontWeight: 600, padding: '4px 10px', borderRadius: '4px',
                background: isCOD ? '#fef3c7' : '#dbeafe', color: isCOD ? '#92400e' : '#1e40af'
              }}>
                {isCOD ? 'COD' : (orderDetails?.paymentMethod || 'Online')}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '14px', color: '#374151' }}>Status</span>
              <span style={{
                fontSize: '12px', fontWeight: 600, padding: '4px 10px', borderRadius: '4px',
                background: isPaymentCollected ? '#d1fae5' : '#fef3c7',
                color: isPaymentCollected ? '#065f46' : '#92400e'
              }}>
                {isPaymentCollected ? 'Paid' : 'Pending'}
              </span>
            </div>
            {isCOD && !isPaymentCollected && (
              <p style={{ fontSize: '13px', color: '#92400e', marginTop: '12px', background: '#fef3c7', padding: '10px', borderRadius: '6px', margin: '12px 0 0' }}>
                💰 Collect <strong>IQD {formatWholeAmount(totalAmount)}</strong>
              </p>
            )}
          </div>
        </div>

        {/* Row 2: 2 columns - Services (wider) | Schedule & Location */}
        <div data-public-grid-row2 style={{
          display: 'grid',
          gridTemplateColumns: '1.5fr 1fr',
          gap: '16px',
          marginBottom: '16px'
        }}>
          {/* Services */}
          <div style={cardStyle}>
            <h3 style={headingStyle}>
              Services
            </h3>
            {allServices.map((svc, idx) => {
              // Only show strikethrough for completed services when there are still active ones (partial completion)
              const isSessionCompleted = isRevisitOrder && !allSessionsComplete && (svc.sessions_completed || 0) >= (svc.number_of_sessions || 1);
              return (
              <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: idx < (allServices.length - 1) ? '1px solid #f3f4f6' : 'none', opacity: isSessionCompleted ? 0.5 : 1 }}>
                <div>
                  <span style={{ fontSize: '14px', color: isSessionCompleted ? '#9ca3af' : '#374151', fontWeight: 500, textDecoration: isSessionCompleted ? 'line-through' : 'none' }}>{svc.service_name}</span>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                    {svc.number_of_sessions} session{svc.number_of_sessions > 1 ? 's' : ''} • {svc.location === 'HOME_VISIT' ? 'Home Visit' : 'On-Site'}
                    {isSessionCompleted && <span style={{ marginLeft: '6px', fontSize: '11px', color: '#065f46', backgroundColor: '#d1fae5', padding: '1px 6px', borderRadius: '4px' }}>Sessions Complete</span>}
                  </div>
                </div>
                <span style={{ fontSize: '14px', fontWeight: 600, color: isSessionCompleted ? '#9ca3af' : '#1f2937', textDecoration: isSessionCompleted ? 'line-through' : 'none' }}>IQD {formatWholeAmount(svc.price || 0)}</span>
              </div>
              );
            })}
            {/* Subtotal */}
            {(activeServices.length > 0) && (transportationFee > 0 || adminFeeAmountPublic > 0) && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderTop: '1px solid #f3f4f6' }}>
                <span style={{ fontSize: '13px', color: '#6b7280' }}>Subtotal</span>
                <span style={{ fontSize: '13px', color: '#6b7280' }}>IQD {formatWholeAmount(servicesSubtotal)}</span>
              </div>
            )}
            {/* Transportation Fee - only for home visit */}
            {isHomeVisit && transportationFee > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderTop: '1px solid #f3f4f6' }}>
                <span style={{ fontSize: '13px', color: '#6b7280' }}>Transportation Fee</span>
                <span style={{ fontSize: '13px', color: '#6b7280' }}>IQD {formatWholeAmount(transportationFee)}</span>
              </div>
            )}
            {/* Admin Fee - shown for both home visit and on-site */}
            {(isRevisitOrder ? computedAdminFee > 0 : adminFeeAmountPublic > 0) && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderTop: '1px solid #f3f4f6' }}>
                <span style={{ fontSize: '13px', color: '#6b7280' }}>Admin Fee ({formatPercent(adminFeePercentagePublic)}%)</span>
                <span style={{ fontSize: '13px', color: '#6b7280' }}>IQD {formatWholeAmount(isRevisitOrder ? computedAdminFee : adminFeeAmountPublic)}</span>
              </div>
            )}
            {/* Loyalty Point Discount — shown as a deduction so the breakdown
                reconciles with the net Total (matches the invoice). */}
            {loyaltyDiscount > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderTop: '1px solid #f3f4f6' }}>
                <span style={{ fontSize: '13px', color: '#6b7280' }}>
                  Loyalty Point Discount{Number(orderDetails?.loyaltyPointsUsed || orderDetails?.loyalty_points_used || 0) > 0 ? ` (${Number(orderDetails?.loyaltyPointsUsed || orderDetails?.loyalty_points_used || 0)} pts)` : ''}
                </span>
                <span style={{ fontSize: '13px', color: '#00BC7D' }}>− IQD {formatWholeAmount(loyaltyDiscount)}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0 0', borderTop: '2px solid #e5e7eb', marginTop: '8px' }}>
              <span style={{ fontSize: '15px', fontWeight: 700, color: '#1f2937' }}>Total</span>
              <span style={{ fontSize: '15px', fontWeight: 700, color: '#099494' }}>IQD {formatWholeAmount(totalAmount)}</span>
            </div>
          </div>

          {/* Schedule & Location */}
          <div style={cardStyle}>
            <h3 style={headingStyle}>
              <FaMapMarkerAlt /> Schedule & Location
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 2px' }}>Date</p>
                <p style={{ fontSize: '14px', fontWeight: 500, color: '#1f2937', margin: 0 }}>
                  {orderDetails?.scheduledDate ? tzDateNumeric(new Date(orderDetails.scheduledDate)) : 'TBD'}
                </p>
              </div>
              <div>
                <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 2px' }}>Time</p>
                <p style={{ fontSize: '14px', fontWeight: 500, color: '#1f2937', margin: 0 }}>
                  {formatTime(orderDetails?.scheduledTime)}
                </p>
              </div>
            </div>
            <div style={{ marginBottom: '12px' }}>
              <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 2px' }}>Location</p>
              <p style={{ fontSize: '14px', fontWeight: 500, color: '#1f2937', margin: 0 }}>
                {isHomeVisit ? 'Home Visit' : 'Visit Center'}
              </p>
            </div>

            {isHomeVisit && orderDetails?.patientAddress && (
              <div style={{ padding: '10px', background: '#f0fdfa', borderRadius: '8px', marginBottom: '12px' }}>
                <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 4px' }}>Patient Address</p>
                <p style={{ fontSize: '13px', color: '#374151', margin: 0 }}>
                  {[
                    orderDetails.patientAddress.houseFlatFloorNo,
                    orderDetails.patientAddress.street,
                    orderDetails.patientAddress.areaDistrict,
                    orderDetails.patientAddress.city,
                    orderDetails.patientAddress.governorate
                  ].filter(Boolean).join(', ')}
                </p>
              </div>
            )}

            {/* Hide "Navigate to Patient" for assessment orders — the therapist
                is going to assess (handled via the sessions page), not to deliver
                a home visit. Shown only for non-assessment home-visit orders. */}
            {isHomeVisit && orderDetails?.patientAddress && !orderDetails?.assessmentRequired && (
              <a
                href={generateNavigationLink(orderDetails.patientAddress)}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                  padding: '12px', borderRadius: '8px',
                  background: '#099494', color: '#fff', textDecoration: 'none',
                  fontSize: '14px', fontWeight: 600
                }}
              >
                <FaTruck /> Navigate to Patient
              </a>
            )}
          </div>
        </div>

        {/* Row 3: Workflow Actions */}
        <div style={cardStyle}>
          <h3 style={headingStyle}>
            <FaClock /> Workflow Actions
          </h3>

          {/* Estimated Arrival Time (show before dispatch) */}
          {!isDispatched && (
            <div style={{ background: '#f9fafb', borderRadius: '8px', padding: '14px', marginBottom: '14px' }}>
              <label style={{ fontSize: '13px', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '8px' }}>
                Estimated Arrival Time
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <input
                  type="number"
                  min="5"
                  max="120"
                  value={estimatedArrivalMinutes}
                  onChange={(e) => setEstimatedArrivalMinutes(parseInt(e.target.value) || 30)}
                  style={{
                    width: '80px', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db',
                    fontSize: '15px', fontWeight: 600, textAlign: 'center'
                  }}
                />
                <span style={{ fontSize: '14px', color: '#6b7280' }}>minutes — Patient will be notified</span>
              </div>
            </div>
          )}

          {isDispatched && orderDetails?.timestamps?.estimatedArrivalTime && !isCompleted && (
            <div style={{ background: '#dbeafe', borderRadius: '8px', padding: '12px 14px', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <FaClock color="#1e40af" />
              <span style={{ fontSize: '14px', color: '#1e40af', fontWeight: 500 }}>
                Estimated Arrival: {tzCustom(new Date(orderDetails.timestamps.estimatedArrivalTime), { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
          )}

          {/* 3-button workflow grid */}
          <div data-public-workflow style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
            <button
              onClick={() => handleWorkflowAction('dispatch')}
              disabled={isDispatched}
              style={{
                padding: '14px', borderRadius: '10px', border: 'none', fontSize: '14px', fontWeight: 600,
                cursor: isDispatched ? 'default' : 'pointer',
                background: isDispatched ? '#d1fae5' : '#099494', color: isDispatched ? '#065f46' : '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                transition: 'all 0.2s'
              }}
            >
              {isDispatched ? <FaCheck /> : <FaTruck />}
              {isDispatched ? 'Dispatched ✓' : 'Mark as Dispatched'}
            </button>

            <button
              onClick={() => handleWorkflowAction('start_process')}
              disabled={!isDispatched || isProcessStarted}
              style={{
                padding: '14px', borderRadius: '10px', border: 'none', fontSize: '14px', fontWeight: 600,
                cursor: (!isDispatched || isProcessStarted) ? 'default' : 'pointer',
                background: isProcessStarted ? '#d1fae5' : (!isDispatched ? '#e5e7eb' : '#2563eb'),
                color: isProcessStarted ? '#065f46' : (!isDispatched ? '#9ca3af' : '#fff'),
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                transition: 'all 0.2s'
              }}
            >
              {isProcessStarted ? <FaCheck /> : <FaPlay />}
              {isProcessStarted ? 'Process Started ✓' : 'Start Process'}
            </button>

            {isCOD ? (
              <button
                onClick={() => handleWorkflowAction('collect_payment')}
                disabled={!isProcessStarted || isCompleted}
                style={{
                  padding: '14px', borderRadius: '10px', border: 'none', fontSize: '14px', fontWeight: 600,
                  cursor: (!isProcessStarted || isCompleted) ? 'default' : 'pointer',
                  background: isCompleted ? '#d1fae5' : (!isProcessStarted ? '#e5e7eb' : '#f59e0b'),
                  color: isCompleted ? '#065f46' : (!isProcessStarted ? '#9ca3af' : '#fff'),
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                  transition: 'all 0.2s'
                }}
              >
                {isCompleted ? <FaCheck /> : <FaMoneyBillWave />}
                {isCompleted ? 'Collected ✓' : 'Mark as Collected'}
              </button>
            ) : (
              <button
                onClick={() => handleWorkflowAction('complete')}
                disabled={!isProcessStarted || isCompleted}
                style={{
                  padding: '14px', borderRadius: '10px', border: 'none', fontSize: '14px', fontWeight: 600,
                  cursor: (!isProcessStarted || isCompleted) ? 'default' : 'pointer',
                  background: isCompleted ? '#d1fae5' : (!isProcessStarted ? '#e5e7eb' : '#10b981'),
                  color: isCompleted ? '#065f46' : (!isProcessStarted ? '#9ca3af' : '#fff'),
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                  transition: 'all 0.2s'
                }}
              >
                <FaCheck />
                {isCompleted ? 'Completed ✓' : 'Mark as Completed'}
              </button>
            )}
          </div>
        </div>

        {/* Completed banner */}
        {isCompleted && (
          <div style={{ marginTop: '20px', padding: '20px', background: '#d1fae5', borderRadius: '12px', textAlign: 'center' }}>
            <FaCheck size={28} color="#065f46" />
            <p style={{ fontSize: '16px', fontWeight: 600, color: '#065f46', margin: '10px 0 0' }}>
              This appointment has been completed
            </p>
          </div>
        )}
      </div>

      {/* Responsive styles */}
      <style>{`
        @media (max-width: 900px) {
          [data-public-grid-row1] { grid-template-columns: 1fr !important; }
          [data-public-grid-row2] { grid-template-columns: 1fr !important; }
          [data-public-workflow] { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

export default PublicPhysioAppointmentDetail;
